'use client';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { WalletClient } from 'viem';

import { walletApi } from '@/services/api/wallet.api';
import { bridgeWithdrawalRelayService } from '@/services/bridgeWithdrawalRelay.service';
import type { BridgeTypedData, TypedData } from '@/types/withdrawal.types';

/**
 * Cache keys for withdrawal queries.
 */
export const withdrawalKeys = {
  all: ['withdrawals'] as const,
  payload: (id: string, owner: string) => [...withdrawalKeys.all, 'payload', id, owner] as const,
};

/**
 * Hook to fetch the EIP-712 payload for a withdrawal.
 * SOLID Principle: Interface Segregation - Components only get the data they need.
 *
 * @param withdrawalId - The Onramper withdrawal ID
 * @param ownerAddress - Resolved owner address from Privy
 */
export function useWithdrawalPayload(withdrawalId: string | null, ownerAddress: string | null) {
  return useQuery({
    queryKey: withdrawalKeys.payload(withdrawalId || '', ownerAddress || ''),
    queryFn: () => walletApi.fetchWithdrawalPayload(withdrawalId!, ownerAddress!),
    enabled: !!withdrawalId && !!ownerAddress,
    staleTime: 0, // Payloads are usually one-time use or short-lived
    gcTime: 1000 * 60 * 5, // Keep in cache for 5 mins
  });
}

/**
 * Mutation to sign the payload and submit it via the Polymarket Relayer.
 * Manages the transition from TypedData -> Signature -> Submission -> Confirmation Hash.
 */
export function useExecuteWithdrawalRelay(accessToken: string) {
  return useMutation({
    mutationFn: async ({
      walletClient,
      typedData,
    }: {
      walletClient: WalletClient;
      typedData: BridgeTypedData;
      accessToken: string;
    }) => {
      if (typedData.primaryType === 'Batch') {
        throw new Error(
          'useExecuteWithdrawalRelay does not handle deposit wallet Batch transactions. ' +
            'Use walletApi.submitBankWithdrawal with signTypedData instead.',
        );
      }
      return bridgeWithdrawalRelayService.submitTransaction(
        walletClient,
        typedData as TypedData,
        accessToken,
      );
    },
  });
}

/**
 * Mutation to notify the backend that the transaction was successfully submitted on-chain.
 */
export function useConfirmWithdrawal() {
  return useMutation({
    mutationFn: async ({
      withdrawalId,
      transactionHash,
      ownerAddress,
    }: {
      withdrawalId: string;
      transactionHash: string;
      ownerAddress: string;
    }) => {
      return walletApi.confirmWithdrawal(withdrawalId, transactionHash, ownerAddress);
    },
  });
}
